import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { posterizeImage } from '../utils/vectorize';
import './ImageView.css';

const ImageView: React.FC = () => {
  const [vectorizedSvg, setVectorizedSvg] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(true);
  const [progress, setProgress] = useState(0);
  const layers = 8;
  const navigate = useNavigate();

  useEffect(() => {
    const image = localStorage.getItem('uploadedImage');
    if (!image) {
      navigate('/');
      return;
    }

    processImage(image);
  }, [navigate]);

  const processImage = async (image: string) => {
    setIsProcessing(true);
    setProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const svg = await posterizeImage(image, {
        steps: 20 // Higher color count for maximum quality
      });

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        setVectorizedSvg(svg);
        localStorage.setItem('vectorizedSvg', svg);
        setIsProcessing(false);
      }, 300);
    } catch (error) {
      console.error('Error vectorizing image:', error);
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    navigate('/export');
  };

  const handleZoomIn = () => {
    console.log('Zoom in');
  };

  const handleZoomOut = () => {
    console.log('Zoom out');
  };

  const handleFitToScreen = () => {
    console.log('Fit to screen');
  };

  const handleFullScreen = () => {
    console.log('Full screen');
  };

  return (
    <div className="imageview-container">
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="tool-btn layers-btn" title="Layers">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2L2 6L10 10L18 6L10 2Z"/>
              <path d="M2 10L10 14L18 10"/>
              <path d="M2 14L10 18L18 14"/>
            </svg>
            <span className="badge">{layers}</span>
          </button>

          <div className="divider"></div>

          <button className="tool-btn" onClick={handleFitToScreen} title="Fit to screen">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="14" height="14" rx="1"/>
              <path d="M7 3V17M13 3V17"/>
            </svg>
          </button>

          <button className="tool-btn" onClick={handleFullScreen} title="Full screen">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 7V3H7M13 3H17V7M17 13V17H13M7 17H3V13"/>
            </svg>
          </button>

          <div className="divider"></div>

          <button className="tool-btn" onClick={handleZoomOut} title="Zoom out">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="9" r="6"/>
              <path d="M14 14L18 18M6 9H12"/>
            </svg>
          </button>

          <button className="tool-btn" onClick={handleZoomIn} title="Zoom in">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="9" r="6"/>
              <path d="M14 14L18 18M9 6V12M6 9H12"/>
            </svg>
          </button>

          <div className="divider"></div>

          <button className="tool-btn" title="Compare">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="2" width="7" height="16"/>
              <rect x="11" y="2" width="7" height="16"/>
            </svg>
          </button>

          <button className="tool-btn" title="Split view">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 2V18M2 2H18V18H2V2Z"/>
            </svg>
          </button>
        </div>

        <div className="toolbar-right">
          <button className="tool-btn" title="Like">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 10L10 6L14 10"/>
            </svg>
          </button>

          <button className="tool-btn" title="Dislike">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 10L10 14L14 10"/>
            </svg>
          </button>

          <button className="download-btn" onClick={handleDownload}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2">
              <path d="M10 3V13M6 9L10 13L14 9"/>
              <path d="M4 17H16"/>
            </svg>
            DOWNLOAD
          </button>
        </div>
      </div>

      <div className="preview-area">
        <div className="result-label">Vectorizer.AI Result</div>
        {isProcessing ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Processing image...</p>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className="progress-text">{progress}%</span>
            </div>
          </div>
        ) : (
          <div className="preview-container">
            <div
              className="svg-preview"
              dangerouslySetInnerHTML={{ __html: vectorizedSvg }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageView;
